import { Boxes, PackageCheck, Route, Truck } from 'lucide-react';

import { defineArea } from '../../app/area';
import { PackingPage } from './pages/PackingPage';
import { PickRoundDetailPage } from './pages/PickRoundDetailPage';
import { PickRoundsPage } from './pages/PickRoundsPage';
import { PickingFlowsPage } from './pages/PickingFlowsPage';
import { ReceiveDetailPage } from './pages/ReceiveDetailPage';
import { ReceiveListPage } from './pages/ReceiveListPage';
import { ShipmentDetailPage } from './pages/ShipmentDetailPage';
import { ShipmentsPage } from './pages/ShipmentsPage';

/**
 * OWNER: the `picking-shipping` module agent (M5 — docs/MODULES.md).
 * Owns: apps/web/src/areas/picking-shipping/**  ·  apps/api/src/modules/picking-shipping/**
 *       packages/shared/src/contracts/picking-shipping.ts
 *       packages/shared/src/i18n/{sv,en,zh}/pickingShipping.json  ·  e2e/picking-shipping.md
 *
 * Owns the portal's goods-reception screen too (spec §5.18): the shipment QR on
 * a pallet label deep-links to `/portal/receive/<qrToken>`, which is why that
 * param route is declared here up front — the link must survive a cold scan
 * from a phone that has never opened the app.
 *
 * The param is the shipment's `qr_token` (a 128-bit secret), NOT its id: a
 * label is a physical object that gets photographed and forwarded, so the
 * printed identifier must not be an enumerable primary key. Membership is
 * re-checked server-side regardless.
 */
export default defineArea({
  id: 'picking-shipping',
  nav: [
    {
      section: 'orders',
      to: '/orders/picking',
      labelKey: 'common:nav.picking.rounds',
      icon: Route,
      permissions: ['picking.rounds.read'],
      order: 30,
    },
    {
      section: 'orders',
      to: '/orders/packing',
      labelKey: 'common:nav.picking.packing',
      icon: Boxes,
      permissions: ['picking.packing'],
      order: 35,
    },
    {
      section: 'orders',
      to: '/orders/shipments',
      labelKey: 'common:nav.picking.shipments',
      icon: Truck,
      permissions: ['shipping.shipments.read'],
      order: 40,
    },
    {
      section: 'warehouse',
      to: '/picking/flows',
      labelKey: 'common:nav.picking.flows',
      icon: Route,
      permissions: ['picking.flows.manage'],
      order: 95,
    },
  ],
  routes: [
    { path: 'orders/picking', element: <PickRoundsPage /> },
    { path: 'orders/picking/:roundId', element: <PickRoundDetailPage /> },
    { path: 'orders/packing', element: <PackingPage /> },
    { path: 'orders/shipments', element: <ShipmentsPage /> },
    { path: 'orders/shipments/:shipmentId', element: <ShipmentDetailPage /> },
    { path: 'picking/flows', element: <PickingFlowsPage /> },
  ],
  portalNav: [
    {
      to: '/portal/receive',
      labelKey: 'shell:portal.receive',
      icon: PackageCheck,
      permissions: ['shipping.reception.confirm'],
      order: 30,
    },
  ],
  portalRoutes: [
    { path: 'receive', element: <ReceiveListPage /> },
    { path: 'receive/:token', element: <ReceiveDetailPage /> },
  ],
});
